import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
// @ts-ignore
import {Robot, Key, Eye, EyeSlash, Trash, ArrowSquareOut, CheckCircle} from "@phosphor-icons/react";
import {
	getStoredKey,
	setStoredKey,
	clearStoredKey,
	maskKey,
	looksLikeAnthropicKey,
	getLastTestedAt,
	testConnection,
	AnthropicError,
} from "../../lib/anthropicClient";

// Page: /settings/anthropic-key
//
// BYO Anthropic key for the "Ask Claude" template generator. The key lives in
// localStorage and is sent only to api.anthropic.com — never to our backend.
// We surface that promise prominently in the subhead.

function fmtRelative(ts) {
	if (!ts) return null;
	const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
	if (sec < 60) return `${sec}s ago`;
	const m = Math.floor(sec / 60);
	if (m < 60) return `${m}m ago`;
	const h = Math.floor(m / 60);
	if (h < 24) return `${h}h ago`;
	const d = Math.floor(h / 24);
	return `${d}d ago`;
}

export default function AnthropicKey() {
	const [storedKey, setStoredKeyState] = useState("");
	const [draft, setDraft] = useState("");
	const [reveal, setReveal] = useState(false);
	const [editing, setEditing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [lastTestedAt, setLastTestedAt] = useState(0);
	const [tickKey, setTickKey] = useState(0);

	useEffect(() => {
		const k = getStoredKey();
		setStoredKeyState(k);
		setEditing(!k); // if no key yet, show the input directly
		setLastTestedAt(getLastTestedAt());
	}, []);

	// Re-render the relative-time string every 30s so "5s ago" doesn't get stale.
	useEffect(() => {
		const id = setInterval(() => setTickKey((k) => k + 1), 30000);
		return () => clearInterval(id);
	}, []);

	const onSave = () => {
		const v = (draft || "").trim();
		if (!v) {
			toast.error("Paste your Anthropic API key first");
			return;
		}
		if (!looksLikeAnthropicKey(v)) {
			toast.error("That doesn't look like an Anthropic key (should start with sk-ant-)");
			return;
		}
		setSaving(true);
		try {
			setStoredKey(v);
			setStoredKeyState(v);
			setDraft("");
			setReveal(false);
			setEditing(false);
			toast.success("Key saved to this browser");
		} catch (err) {
			toast.error(err && err.message ? err.message : "Could not save key");
		} finally {
			setSaving(false);
		}
	};

	const onTest = async () => {
		const candidate = (draft || storedKey || "").trim();
		if (!candidate) {
			toast.error("Paste your key first");
			return;
		}
		if (!looksLikeAnthropicKey(candidate)) {
			toast.error("That doesn't look like an Anthropic key (should start with sk-ant-)");
			return;
		}
		setTesting(true);
		try {
			await testConnection({key: candidate});
			setLastTestedAt(Date.now());
			toast.success("Key works — connected to Anthropic");
		} catch (err) {
			const msg = err instanceof AnthropicError
				? err.message
				: (err && err.message) || "Connection test failed";
			toast.error(msg);
		} finally {
			setTesting(false);
		}
	};

	const onRemove = () => {
		const ok = window.confirm(
			"Remove your Anthropic key from this browser? You'll need to paste it again to use Ask Claude.",
		);
		if (!ok) return;
		clearStoredKey();
		setStoredKeyState("");
		setDraft("");
		setReveal(false);
		setEditing(true);
		setLastTestedAt(0);
		toast.success("Key removed from this browser");
	};

	const onReplace = () => {
		setDraft("");
		setReveal(false);
		setEditing(true);
	};

	const lastTestedLabel = lastTestedAt ? fmtRelative(lastTestedAt) : null;
	// Reference tickKey so the lint/hooks rules pick up the dependency.
	void tickKey;

	return (
		<div className="max-w-3xl mx-auto px-6 py-10">
			{/* Header */}
			<div className="flex items-start gap-4 mb-6">
				<div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
					<Robot size={26} weight="duotone"/>
				</div>
				<div className="min-w-0">
					<h1 className="text-3xl font-semibold text-slate-900">Anthropic API key</h1>
					<p className="text-slate-500 text-sm mt-1">
						Used to generate templates with Claude. Stored only in your browser — we never see it.
					</p>
				</div>
			</div>

			<div className="rounded-2xl shadow-sm border border-slate-100 bg-white p-6">
				{/* Saved-key summary (shown when a key exists and we're not editing) */}
				{storedKey && !editing && (
					<div className="space-y-4">
						<div className="flex items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
							<div className="flex items-center gap-3 min-w-0">
								<div className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-emerald-600 flex items-center justify-center shrink-0">
									<Key size={18} weight="duotone"/>
								</div>
								<div className="min-w-0">
									<div className="font-mono text-sm text-slate-900 truncate">
										{maskKey(storedKey)}
									</div>
									<div className="text-xs text-slate-500 mt-0.5">
										{lastTestedLabel
											? <>Last tested {lastTestedLabel}</>
											: <>Not yet tested</>}
									</div>
								</div>
							</div>
							<div className="flex items-center gap-2 shrink-0">
								<button
									type="button"
									onClick={onTest}
									disabled={testing}
									className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
									{testing ? "Testing…" : (
										<>
											<CheckCircle size={15} weight="bold"/>
											Test
										</>
									)}
								</button>
								<button
									type="button"
									onClick={onReplace}
									className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-4 py-2 text-sm font-medium transition">
									Replace
								</button>
								<button
									type="button"
									onClick={onRemove}
									aria-label="Remove key"
									className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition">
									<Trash size={15} weight="bold"/>
									<span className="hidden sm:inline">Remove</span>
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Input form (shown when no key yet OR user clicked Replace) */}
				{(editing || !storedKey) && (
					<div>
						<label htmlFor="anthropic-key-input" className="block text-sm font-medium text-slate-700 mb-1.5">
							Paste your key
						</label>
						<div className="relative">
							<input
								id="anthropic-key-input"
								type={reveal ? "text" : "password"}
								value={draft}
								onChange={(e) => setDraft(e.target.value)}
								placeholder="sk-ant-api03-…"
								autoComplete="off"
								spellCheck={false}
								className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-11 font-mono text-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition"/>
							<button
								type="button"
								onClick={() => setReveal((r) => !r)}
								aria-label={reveal ? "Hide key" : "Show key"}
								className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition">
								{reveal ? <EyeSlash size={18}/> : <Eye size={18}/>}
							</button>
						</div>
						<p className="text-xs text-slate-500 mt-2">
							Stays in this browser only (localStorage). Calls go straight to api.anthropic.com.
						</p>

						<div className="flex flex-wrap items-center gap-2 mt-5">
							<button
								type="button"
								onClick={onSave}
								disabled={saving || !draft.trim()}
								className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition">
								{saving ? "Saving…" : "Save"}
							</button>
							<button
								type="button"
								onClick={onTest}
								disabled={testing || !draft.trim()}
								className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-3 font-medium inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
								{testing ? "Testing…" : "Test connection"}
							</button>
							{storedKey && (
								<button
									type="button"
									onClick={() => { setEditing(false); setDraft(""); setReveal(false); }}
									className="rounded-full px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 transition">
									Cancel
								</button>
							)}
						</div>
					</div>
				)}

				{/* Where to get a key */}
				<div className="mt-8 pt-6 border-t border-slate-100">
					<h3 className="text-sm font-semibold text-slate-900 mb-1">Don't have a key yet?</h3>
					<p className="text-sm text-slate-600 mb-3">
						Create one in your Anthropic console — pricing is per-token and you keep full control of usage limits.
					</p>
					<a
						href="https://console.anthropic.com/settings/keys"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800">
						console.anthropic.com/settings/keys
						<ArrowSquareOut size={14} weight="bold"/>
					</a>
				</div>
			</div>
		</div>
	);
}
